import {
  GainType,
  GameEngines,
  GameSystems,
  IArmyBook,
  IDataSpecialRule,
  ISpell,
  IUnit,
  IUpgradeGains,
  IUpgradeGainsItem,
  IUpgradeGainsRule,
  IUpgradeGainsWeapon,
  IUpgradeOption,
  IUpgradePackage,
  IUpgradeSection,
  IWarfleetsUnit,
  IWeapon,
  OfficialBookRulesResponse,
  RuleGenParamValue,
  RuleId,
  ShipClass,
  UnitType,
  WeaponType,
  WeaponUsage,
  WeaponWithUsage
} from "@/data/types";
import { nanoid } from "nanoid";
import { getUnitRules, getUnitWeapons, isItem, isRule, isWeapon } from "../Helpers";
import ArmyBookRule from "./ArmyBookRule";
import ArmyBookWeapon from "./ArmyBookWeapon";

export default class ArmyBookUtils {
  public static findUniqueRulesInArmyBook(armyBook: {
    name: string;
    units: IUnit[];
    upgradePackages: IUpgradePackage[];
    spells: ISpell[];
  }): RuleId[] {
    try {
      const rules = [];
      if (armyBook.units) {
        rules.push(...ArmyBookUtils.findUsedRulesInUnits(armyBook.units));
      }
      if (armyBook.upgradePackages) {
        rules.push(...ArmyBookUtils.findUsedRulesInPackages(armyBook.upgradePackages));
      }
      if (armyBook.spells?.length > 0) {
        rules.push(...ArmyBookUtils.findUsedRulesInSpells(armyBook.spells));
      }
      rules.sort();
      return [...new Set(rules)];
    } catch (e) {
      console.error("findUniqueRulesInArmyBook: " + armyBook.name, e);
      return [];
    }
  }

  public static findUsedRulesInPackages(packs: IUpgradePackage[]): RuleId[] {
    return packs.flatMap((x) => this.findUsedRulesInPackage(x));
  }
  public static findUsedRulesInSpells(spells: ISpell[]): RuleId[] {
    return spells.flatMap((x) => this.findUsedRulesInSpell(x));
  }

  public static findUsedRulesInUnits(units: IUnit[]): RuleId[] {
    const results = [];
    units.forEach((unit) => {
      results.push(...this.findUsedRulesInUnit(unit));
    });
    return results;
  }

  public static findUniqueWeaponsInArmyBook(armyBook: IArmyBook) {
    const equipmentToWeapon = (e: any) =>
      new ArmyBookWeapon(
        e.name,
        e.range || 0,
        e.attacks,
        e.specialRules.map((r) => (typeof r === "string" ? ArmyBookRule.FromString(r) : r)),
        e.count,
        e.weaponId,
        e.id
      );

    const weapons = armyBook.units.flatMap((x) =>
      getUnitWeapons(x).map((e: IUpgradeGainsWeapon) => equipmentToWeapon(e))
    );

    const upgradeWeapons = armyBook.upgradePackages.flatMap((pkg) => {
      return pkg.sections.flatMap((section) => {
        return section.options.flatMap((option) => {
          return option.gains.flatMap((gain) => {
            if (gain.type === GainType.Item) {
              return (gain as IUpgradeGainsItem).content
                .filter((x) => x.type === GainType.Weapon)
                .map((x) => equipmentToWeapon(x));
            } else if (gain.type === GainType.Weapon) {
              return [equipmentToWeapon(gain)];
            }
            return [];
          });
        });
      });
    });

    return distinctBy(weapons.concat(upgradeWeapons as any), (x) => x.toString());
  }

  public static attachWeaponUsage(
    weapons: IWeapon[],
    books: { uid: string; name: string; units: IUnit[]; upgradePackages: IUpgradePackage[] }[]
  ): WeaponWithUsage[] {
    const weaponsWithUsage = weapons as WeaponWithUsage[];

    // Create weapon ID → weapon map for O(1) lookups
    const weaponMap = new Map(weaponsWithUsage.map((w) => [w.id, w]));

    // Initialize usage for all weapons
    for (const weapon of weaponsWithUsage) {
      weapon.usage = {
        units: [],
        packages: [],
        aliases: [],
        multipliedAliases: [],
        books: []
      };
    }

    // Build usage index: weaponId → { units, packages, books }
    const usageIndex = new Map<string, { units: any[]; packages: any[]; books: Set<string> }>();

    // Single pass through all books
    for (const book of books) {
      for (const unit of book.units) {
        for (const weapon of unit.weapons || (unit as any).equipment || []) {
          if (!usageIndex.has(weapon.weaponId)) {
            usageIndex.set(weapon.weaponId, { units: [], packages: [], books: new Set() });
          }
          const usage = usageIndex.get(weapon.weaponId)!;
          usage.units.push({ name: unit.name, id: unit.id, book: book.uid });
          usage.books.add(book.uid);
        }
      }

      for (const pkg of book.upgradePackages) {
        for (const section of pkg.sections) {
          for (const option of section.options) {
            for (const gain of option.gains) {
              const checkGain = (g: any) => {
                if (isWeapon(g)) {
                  if (!usageIndex.has(g.weaponId)) {
                    usageIndex.set(g.weaponId, { units: [], packages: [], books: new Set() });
                  }
                  const usage = usageIndex.get(g.weaponId)!;
                  usage.packages.push({ id: pkg.uid, book: book.uid, name: pkg.hint });
                  usage.books.add(book.uid);
                }
              };

              checkGain(gain);
              if (isItem(gain)) {
                gain.content.forEach(checkGain);
              }
            }
          }
        }
      }
    }

    // Apply usage data and handle aliases
    for (const weapon of weaponsWithUsage) {
      if (weapon.parentId) {
        const parent = weaponMap.get(weapon.parentId);
        if (parent && !parent.usage.aliases.some((x) => x.id === weapon.id)) {
          parent.usage.aliases.push({
            name: weapon.name,
            id: weapon.id
          });
        }
      }

      const usage = usageIndex.get(weapon.id);
      if (usage) {
        weapon.usage.units = usage.units;
        weapon.usage.packages = usage.packages;
        weapon.usage.books = Array.from(usage.books).map((uid) => {
          const book = books.find((b) => b.uid === uid);
          return { uid, name: book?.name || "" };
        });
      }
    }

    for (const weapon of weaponsWithUsage) {
      if (weapon.type !== WeaponType.Template) continue;
      // Find all aliased of this weapon, and combine the usage onto this weapon's usage
      const aliases = weaponsWithUsage.filter((w) => w.parentId === weapon.id);
      const result = Object.keys(weapon.usage).reduce(
        (acc, key) => ({
          ...acc,
          [key]: aliases.reduce((section, next) => [...section, ...next.usage[key]], [])
        }),
        {
          ...weapon.usage
        } as WeaponUsage
      );

      weapon.usage = result;
    }

    return weaponsWithUsage;
  }

  public static getWeaponUsage(
    weapon,
    armyBooks: {
      uid: string;
      name: string;
      enabledGameSystems: GameSystems[];
      units: IUnit[];
      upgradePackages: IUpgradePackage[];
    }[]
  ) {
    const units = [];
    const packages = [];
    const books = [];

    for (let book of armyBooks || []) {
      let bookAdded = false;
      const addBook = () => {
        if (!bookAdded) {
          books.push({
            uid: book.uid,
            name: book.name,
            enabledGameSystems: book.enabledGameSystems
          });

          bookAdded = true;
        }
      };

      for (let unit of book.units) {
        if ((unit.weapons || (unit as any).equipment).some((x) => x.weaponId === weapon.id)) {
          units.push({
            name: unit.name,
            id: unit.id,
            book: book.uid
          });
          addBook();
        }
      }

      for (let pkg of book.upgradePackages) {
        const isMatch = () => {
          for (let section of pkg.sections)
            for (let option of section.options)
              for (let gain of option.gains) {
                if (isWeapon(gain) && gain.weaponId === weapon.id) {
                  return true;
                }
                if (isItem(gain)) {
                  for (let c of gain.content) {
                    if (isWeapon(c) && c.weaponId === weapon.id) {
                      return true;
                    }
                  }
                }
              }
          return false;
        };

        if (isMatch()) {
          addBook();
          packages.push({
            id: pkg.uid,
            book: book.uid,
            name: pkg.hint
          });
        }
      }
    }

    return { units, packages, books };
  }

  public static getRuleUsage(
    ruleId: string,
    ruleName: string,
    allRules: IDataSpecialRule[],
    armyBooks: OfficialBookRulesResponse[]
  ) {
    const units = [];
    const packages = [];
    const spells = [];
    const books = [];

    for (let book of armyBooks || []) {
      let bookAdded = false;
      const addBook = () => {
        if (!bookAdded) {
          books.push({
            uid: book.uid,
            name: book.name,
            enabledGameSystems: book.enabledGameSystems
          });

          bookAdded = true;
        }
      };

      for (const unit of book.units) {
        if (unit.rules.some((x) => x === ruleId)) {
          units.push({
            name: unit.name,
            id: unit.id,
            book: book.uid
          });
          addBook();
        }
      }

      for (const pkg of book.upgradePackages) {
        if (pkg.rules.some((x) => x === ruleId)) {
          packages.push({
            name: pkg.name,
            id: pkg.id,
            book: book.uid
          });
          addBook();
        }
      }

      for (const spell of book.spells) {
        if (spell.rules.some((x) => x === ruleId)) {
          spells.push({
            name: spell.name,
            id: spell.id,
            book: book.uid
          });
          addBook();
        }
      }
    }

    const aliases = allRules
      .filter((x) => x.aliasedRuleId === ruleId)
      .map((x) => ({ id: x.id, name: x.name }));

    const genReferences: { id: string; name: string }[] = [];
    for (const generatedRule of allRules) {
      if (!generatedRule.generation) continue;

      let referencesRule = false;
      for (const section of Object.values(generatedRule.generation)) {
        for (const parameter of section.parameters) {
          if (parameter.value?.id === ruleId) {
            referencesRule = true;
            break;
          }
        }
        if (referencesRule) break;
      }

      if (referencesRule) {
        genReferences.push({ id: generatedRule.id, name: generatedRule.name });
      }
    }

    const formulaReferences = allRules
      .filter((x) => !x.aliasedRuleId && Array.isArray(x.costFormula))
      .filter((x) => (x.costFormula ?? []).some((formula) => String(formula.value).includes(ruleName)))
      .map((x) => ({ id: x.id, name: x.name }));

    return { units, packages, spells, books, aliases, genReferences, formulaReferences };
  }

  public static getDefaultUnit(name = "", engine: GameEngines): IUnit {
    const unit = {
      id: nanoid(7),
      name,
      size: engine === GameEngines.Warfleets ? 3 : 1,
      quality: 4,
      defense: 4,
      weapons: [],
      rules: [],
      upgrades: [],
      cost: 0,
      type: UnitType.Default,
      bases: {
        round: "",
        square: ""
      },
      items: []
    };

    if (engine === GameEngines.Warfleets) {
      const wfUnit = unit as IWarfleetsUnit;
      wfUnit.class = ShipClass.Medium;
      wfUnit.evasion = 4;
      wfUnit.move = 6;
      wfUnit.cruise = 9;
    }

    return unit;
  }

  private static findUsedRulesInItem(item: IUpgradeGainsItem): { id: RuleId }[] {
    return item.content.flatMap((x) => (isRule(x) ? [x as any] : isWeapon(x) ? x.specialRules || [] : []));
  }

  private static findUsedRulesInOption(option: IUpgradeOption): RuleId[] {
    return option.gains
      .flatMap((x: IUpgradeGains) => {
        if (isItem(x)) {
          return this.findUsedRulesInItem(x as IUpgradeGainsItem);
        }
        if (isRule(x)) {
          return [x as IUpgradeGainsRule];
        }
        if (isWeapon(x)) {
          return (x.specialRules || []) as { id: RuleId }[];
        }
      })
      .map((x) => x.id);
  }

  private static findUsedRulesInSection(section: IUpgradeSection): RuleId[] {
    return section.options.flatMap((x) => this.findUsedRulesInOption(x));
  }

  public static findUsedRulesInPackage(pack: IUpgradePackage): RuleId[] {
    return pack.sections.flatMap((x) => this.findUsedRulesInSection(x));
  }

  public static findUsedRulesInSpell(spell: ISpell): RuleId[] {
    if (!spell.generation) return [];

    const parameters = spell.generation.effect.parameters.filter(
      (x) => x.name.startsWith("unitRule") || x.name.startsWith("weaponRule")
    );
    return parameters.map((x) => (x.value as RuleGenParamValue).id);
  }

  public static findUsedRulesInUnit(unit: IUnit): RuleId[] {
    const weapons = getUnitWeapons(unit);
    const rules = getUnitRules(unit);

    return rules
      .map((x) => x.id)
      .concat(weapons.flatMap((x) => x.specialRules.map((r) => ArmyBookRule.Parse(r)?.id)));
  }
}

function distinctBy<T>(arr: T[], selector: (x: T) => any) {
  const seen = [];
  const results: T[] = [];
  for (let i = 0; i < arr.length; i++) {
    const key = selector(arr[i]);
    if (seen.findIndex((x) => x === key) < 0) {
      results.push(arr[i]);
      seen.push(key);
    }
  }
  return results;
}
